"use client";

import { Topbar } from "@/components/shell/topbar";
import { LiveTranscript } from "./live-transcript";
import { AiCoach } from "./ai-coach";
import { useSpeechRecognition } from "@/lib/use-speech-recognition";
import { useEffect, useRef } from "react";
import { useCallStore } from "@/store/call-store";

export function CallWorkspace() {
  const { isListening, transcript, start, stop, error } = useSpeechRecognition();
  const addTranscriptLine = useCallStore((s) => s.addTranscriptLine);
  const setRecommendation = useCallStore((s) => s.setRecommendation);
  const setCoaching = useCallStore((s) => s.setCoaching);
  const isCoaching = useCallStore((s) => s.isCoaching);
  const elapsed = useCallStore((s) => s.elapsed);
  const setElapsed = useCallStore((s) => s.setElapsed);
  const updateWaveform = useCallStore((s) => s.updateWaveform);
  const running = useCallStore((s) => s.running);

  const prevTranscript = useRef(transcript);
  
  // Timer and Waveform loop
  useEffect(() => {
    let frame = 0;
    let last = performance.now();

    const loop = (now: number) => {
      const delta = Math.min(0.25, (now - last) / 1000);
      last = now;
      if (running) {
        setElapsed(elapsed + delta);
      }
      // Update waveform based on listening state
      updateWaveform(isListening);
      frame = requestAnimationFrame(loop);
    };

    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [running, elapsed, isListening, setElapsed, updateWaveform]);

  const callMode = useCallStore((s) => s.callMode);
  const currentSpeaker = useCallStore((s) => s.currentSpeaker);

  // Handle new transcript segments
  useEffect(() => {
    if (transcript !== prevTranscript.current && transcript.trim()) {
      // Find the new part of the transcript
      const newText = transcript.replace(prevTranscript.current, "").trim();
      
      if (newText) {
        const activeSpeaker = callMode === "meet" ? "rep" : currentSpeaker;
        const speakerName = activeSpeaker === "rep" ? "You" : "Client";

        addTranscriptLine({
          speaker: activeSpeaker,
          name: speakerName,
          text: newText,
        });

        // Trigger coaching only if in meet mode OR if in phone mode and client is speaking
        if (callMode === "meet" || (callMode === "phone" && currentSpeaker === "customer")) {
          fetchCoaching(transcript);
        }
      }
      
      prevTranscript.current = transcript;
    }
  }, [transcript, addTranscriptLine, callMode, currentSpeaker]);

  async function fetchCoaching(fullTranscript: string) {
    const apiKey = localStorage.getItem("varta_groq_key");
    if (!apiKey) return;

    setCoaching(true);
    try {
      const systemPrompt = `You are an expert sales coach listening in on a live B2B SaaS sales call.
The transcript is generated by an imperfect speech-to-text engine and may contain phonetic misspellings, gibberish, or Hindi words (Hinglish) forced into English text. Use your intelligence to infer the true meaning of the conversation despite any transcription errors.

Your goal is to analyze the recent transcript and provide actionable, real-time advice to the sales rep.
Provide your response strictly in JSON format matching this structure:
{
  "intent": "Brief description of the customer's core intent or objection",
  "reasoning": "Why you are suggesting this approach",
  "response": "The full suggested response for the rep",
  "alternative": "A different approach if the first doesn't fit",
  "short": "A very brief, punchy version of the response (max 10 words)",
  "expectedOutcome": "What this response should achieve",
  "confidence": 95,
  "tone": "Confident",
  "approach": "Reframe to ROI",
  "strategy": "Objection -> Proof",
  "nextBestQuestion": "What is the best next question to ask?"
}

Return ONLY valid JSON, no markdown formatting.`;

      const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: "llama-3.1-8b-instant",
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: `Recent transcript:\n\n${fullTranscript}` },
          ],
          response_format: { type: "json_object" },
          temperature: 0.3,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const content = data.choices[0]?.message?.content;
        if (content) {
          setRecommendation(JSON.parse(content));
        }
      } else {
        console.error("Groq API error", await res.text());
        setCoaching(false);
      }
    } catch (err) {
      console.error("Coaching failed", err);
      setCoaching(false);
    }
  }

  return (
    <div className="varta-ambient flex h-screen overflow-hidden flex-col">
      <Topbar startListening={start} stopListening={stop} isListening={isListening} />

      <main className="scroll-thin flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
        <div className="mx-auto flex h-full max-w-5xl flex-col gap-6">
          
          {error && (
            <div className="rounded-xl border border-danger/30 bg-danger-soft p-4 text-sm text-danger shadow-sm">
              {error}
            </div>
          )}

          {/* Workspace: transcript · coach */}
          <div className="grid h-full grid-cols-1 gap-6 lg:grid-cols-2">
            <LiveTranscript className="h-full min-h-[400px] shadow-sm rounded-2xl" />
            <AiCoach className="h-full min-h-[400px] shadow-sm rounded-2xl" isCoaching={isCoaching} />
          </div>

        </div>
      </main>
    </div>
  );
}
